/**
 * handoffActions.ts — Centralized Hand-off Action Resolver (Phase 2)
 *
 * Translates a (possibly under-specified) FollowUpTask hand-off into an
 * `ExecutableAction` ready to feed `runAction()` from src/lib/actionRunner.ts.
 *
 * Design principles:
 *  1. ONE execution primitive across the platform (`ExecutableAction`).
 *     The inbox does not invent its own runner — it reuses the same shape
 *     Operations Command Center, Dispatch Intelligence, and the Next-Step
 *     toast already use.
 *  2. EXPLICIT intent always wins. If `task.handoffIntent` is set, we use
 *     it. Otherwise we infer it from source linkage + assignee role.
 *     Inference is centralized here — never scattered across dashboards.
 *  3. PERMISSIVE storage, STRICT runtime. `actionPayload` is `unknown` on
 *     the task but narrowed via a discriminated union before use.
 *  4. SAFE FALLBACK. Unknown intents resolve to a "open source object"
 *     navigation, never crashing.
 *  5. MATURITY READY. The `HandoffActionRunners` shape parallels
 *     `NextStepRunners` so future Maverick automation can drive the same
 *     resolver to auto-launch safe actions.
 */

import type { NavigateFunction } from "react-router-dom";
import type { ExecutableAction } from "@/lib/actionRunner";
import type {
  FollowUpTask,
  HandoffIntent,
  HandoffCompletionOutcome,
} from "./opportunityTypes";
import type { Job, Issue } from "./types";
import type { UserRole } from "./RoleContext";

// ─────────────────────────────────────────────────────────────────────────────
// Internal payload contract — discriminated union narrowed from `unknown`.
// Storage stays permissive; runtime stays type-safe.
// ─────────────────────────────────────────────────────────────────────────────

type NarrowedPayload =
  | { kind: "job"; jobId: string }
  | { kind: "issue"; issueId: string; jobId?: string }
  | { kind: "estimate"; estimateId: string }
  | { kind: "action"; actionId: string }
  | { kind: "none" };

function narrowPayload(task: FollowUpTask): NarrowedPayload {
  // Explicit payload first.
  const p = task.actionPayload as Record<string, unknown> | undefined;
  if (p && typeof p === "object") {
    if (typeof p.jobId === "string" && typeof p.issueId === "string") {
      return { kind: "issue", issueId: p.issueId, jobId: p.jobId };
    }
    if (typeof p.issueId === "string") return { kind: "issue", issueId: p.issueId };
    if (typeof p.jobId === "string") return { kind: "job", jobId: p.jobId };
    if (typeof p.estimateId === "string") return { kind: "estimate", estimateId: p.estimateId };
    if (typeof p.actionId === "string") return { kind: "action", actionId: p.actionId };
  }
  // Fall back to source linkage on the task itself.
  if (task.issueId) return { kind: "issue", issueId: task.issueId, jobId: task.jobId };
  if (task.jobId) return { kind: "job", jobId: task.jobId };
  if (task.estimateId) return { kind: "estimate", estimateId: task.estimateId };
  if (task.actionId) return { kind: "action", actionId: task.actionId };
  // Legacy: opportunityId may anchor a job.
  if (task.opportunityId?.startsWith("JOB-")) {
    return { kind: "job", jobId: task.opportunityId };
  }
  return { kind: "none" };
}

// ─────────────────────────────────────────────────────────────────────────────
// Intent inference — single source of truth.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Infer the intent of a hand-off when none is explicitly declared.
 * Pure, deterministic, side-effect-free. Explicit intent ALWAYS wins —
 * callers should check `task.handoffIntent ?? inferIntent(task)`.
 */
export function inferIntent(task: FollowUpTask): HandoffIntent {
  // Source-driven inference (most specific signal first).
  if (task.issueId) return "service_resolution";
  if (task.estimateId) return "estimate_revision";

  // Job-anchored inference, refined by assignee role.
  if (task.jobId || task.opportunityId?.startsWith("JOB-")) {
    const role = task.assignedToRoleKey as UserRole | undefined;
    if (role === "warehouse_manager" || role === "inventory_manager" || role === "procurement_manager") {
      return "material_confirmation";
    }
    if (role === "head_of_installation" || role === "installer" || role === "ops_manager") {
      return "install_review";
    }
    if (role === "cfo" || role === "finance_manager" || role === "bookkeeper") {
      return "finance_followup";
    }
    if (role === "sales" || role === "sales_manager") {
      return "schedule_coordination";
    }
    return "install_review";
  }

  // Lead-anchored hand-offs default to outbound contact.
  if (task.opportunityId?.startsWith("OPP-") || task.opportunityId?.startsWith("LEAD-")) {
    return "customer_contact";
  }

  return "general_handoff";
}

/**
 * The effective intent for a task — explicit if set, otherwise inferred.
 * Use this everywhere you need to render or branch on intent.
 */
export function getEffectiveIntent(task: FollowUpTask): HandoffIntent {
  return task.handoffIntent ?? inferIntent(task);
}

// ─────────────────────────────────────────────────────────────────────────────
// Display labels — kept here so dashboards never invent their own.
// ─────────────────────────────────────────────────────────────────────────────

export const INTENT_LABELS: Record<HandoffIntent, string> = {
  install_review: "Install Review",
  material_confirmation: "Material Confirmation",
  schedule_coordination: "Schedule Coordination",
  finance_followup: "Finance Follow-Up",
  service_resolution: "Service Resolution",
  estimate_revision: "Estimate Revision",
  permit_followup: "Permit Follow-Up",
  customer_contact: "Customer Contact",
  general_handoff: "Hand-off",
};

const INTENT_DEFAULT_CTA: Record<HandoffIntent, string> = {
  install_review: "Review Install",
  material_confirmation: "Review Materials",
  schedule_coordination: "Open Schedule",
  finance_followup: "Open Financials",
  service_resolution: "Resolve Issue",
  estimate_revision: "Open Estimate",
  permit_followup: "Open Permits",
  customer_contact: "Open Customer",
  general_handoff: "Open",
};

// ─────────────────────────────────────────────────────────────────────────────
// Modal runners — parallels NextStepRunners so the inbox can prefer modal
// execution when the host page has the right modal wired up.
// ─────────────────────────────────────────────────────────────────────────────

export interface HandoffActionRunners {
  openOrdering?: (job: Job) => void;
  openReschedule?: (job: Job) => void;
  openQuickAssign?: (job: Job) => void;
  openResolveIssue?: (job: Job, issue?: Issue) => void;
}

export interface ResolveHandoffActionContext {
  navigate: NavigateFunction;
  /** Lookup helpers — optional. When absent we fall back to navigation. */
  jobs?: Job[];
  issues?: Issue[];
  runners?: HandoffActionRunners;
}

// ─────────────────────────────────────────────────────────────────────────────
// Core resolver — returns an ExecutableAction for runAction().
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolve the primary CTA for a hand-off into an ExecutableAction.
 *
 * Resolution order:
 *   1. Explicit `actionLabel` overrides the CTA label (never the route).
 *   2. Intent → modal (if a runner is available for that intent).
 *   3. Intent → route (deep-link to the source operational object).
 *   4. Safe fallback: navigate to source object's detail page.
 *   5. Last resort: navigate to /follow-ups so the task is never orphaned.
 */
export function resolveHandoffAction(
  task: FollowUpTask,
  ctx: ResolveHandoffActionContext,
): ExecutableAction {
  const intent = getEffectiveIntent(task);
  const payload = narrowPayload(task);
  const runners = ctx.runners ?? {};
  const ctaLabel = task.actionLabel ?? INTENT_DEFAULT_CTA[intent];

  // Resolve a job reference if we have one — needed for modal launchers.
  const job =
    payload.kind === "job"
      ? ctx.jobs?.find((j) => j.id === payload.jobId)
      : payload.kind === "issue" && payload.jobId
        ? ctx.jobs?.find((j) => j.id === payload.jobId)
        : undefined;

  // 1. Modal-first execution when host page provides a runner + we have a job.
  if (job) {
    if (intent === "material_confirmation" && runners.openOrdering) {
      return { label: ctaLabel, executionMode: "modal", openModal: () => runners.openOrdering!(job) };
    }
    if (intent === "schedule_coordination" && runners.openReschedule) {
      return { label: ctaLabel, executionMode: "modal", openModal: () => runners.openReschedule!(job) };
    }
    if (intent === "install_review" && runners.openQuickAssign) {
      return { label: ctaLabel, executionMode: "modal", openModal: () => runners.openQuickAssign!(job) };
    }
    if (intent === "service_resolution" && runners.openResolveIssue) {
      const issue =
        payload.kind === "issue"
          ? ctx.issues?.find((i) => i.id === payload.issueId)
          : undefined;
      return {
        label: ctaLabel,
        executionMode: "modal",
        openModal: () => runners.openResolveIssue!(job, issue),
      };
    }
  }

  // 2. Navigate to the source operational object.
  switch (payload.kind) {
    case "issue":
      return { label: ctaLabel, executionMode: "navigate", route: `/issues` };
    case "job":
      return { label: ctaLabel, executionMode: "navigate", route: `/jobs/${payload.jobId}` };
    case "estimate":
      return { label: ctaLabel, executionMode: "navigate", route: `/quotes/${payload.estimateId}` };
    case "action":
      return { label: ctaLabel, executionMode: "navigate", route: `/follow-ups` };
    case "none":
    default:
      // Lead-anchored opportunity?
      if (task.opportunityId && !task.opportunityId.startsWith("JOB-")) {
        return { label: ctaLabel, executionMode: "navigate", route: `/lead/${task.opportunityId}` };
      }
      return { label: ctaLabel, executionMode: "navigate", route: `/follow-ups` };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Convenience: secondary "open source" link metadata for inbox cards.
// Kept separate from the primary action so the card can render both without
// the primary CTA collapsing into a generic "Open job" link.
// ─────────────────────────────────────────────────────────────────────────────

export interface HandoffSourceLink {
  href: string;
  label: string;
  refId: string;
}

export function getSourceLink(task: FollowUpTask): HandoffSourceLink | null {
  if (task.issueId) {
    return { href: `/issues`, label: "Open issue", refId: task.issueId };
  }
  if (task.jobId) {
    return { href: `/jobs/${task.jobId}`, label: "Open job", refId: task.jobId };
  }
  if (task.estimateId) {
    return { href: `/quotes/${task.estimateId}`, label: "Open estimate", refId: task.estimateId };
  }
  if (task.opportunityId?.startsWith("JOB-")) {
    return { href: `/jobs/${task.opportunityId}`, label: "Open job", refId: task.opportunityId };
  }
  if (task.opportunityId) {
    return { href: `/lead/${task.opportunityId}`, label: "Open lead", refId: task.opportunityId };
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Completion helper — applies a structured outcome to a task immutably.
// Phase-2 UI uses only `completed` and `completed_and_created_followup`,
// but this helper accepts the full outcome union so future surfaces can
// reuse it without re-implementing the shape.
// ─────────────────────────────────────────────────────────────────────────────

export function applyCompletionOutcome(
  task: FollowUpTask,
  outcome: HandoffCompletionOutcome = "completed",
): FollowUpTask {
  return {
    ...task,
    status: "completed",
    completedAt: new Date().toISOString(),
    completionOutcome: outcome,
  };
}
